import React, { useState, useRef } from 'react';
import {
  Dialog,
  Button,
  Box,
  Alert,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Stack,
  Chip,
  Divider,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Info as InfoIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { DialogHeader } from '../../common/AppDialog';
import { parseImportedRulesCSV, validateRuleCategories, getCSVTemplate } from '../../../utils/rulesExportUtils';

const RulesImportDialog = ({
  open,
  onClose,
  categories,
  onImport,
  isLoading = false
}) => {
  const [file, setFile] = useState(null);
  const [selectedRules, setSelectedRules] = useState([]);
  const [parseError, setParseError] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [parsedRules, setParsedRules] = useState([]);
  const [step, setStep] = useState('upload'); // 'upload' | 'review' | 'importing'
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setParseError(null);
    setWarnings([]);
    setSelectedRules([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        const rules = parseImportedRulesCSV(content);
        const { validRules, warnings: categoryWarnings } = validateRuleCategories(rules, categories);

        setParsedRules(validRules);
        setWarnings(categoryWarnings);

        if (validRules.length > 0) {
          setSelectedRules(validRules.map((_, idx) => idx));
          setStep('review');
        } else if (categoryWarnings.length > 0) {
          setParseError('No valid rules found. All rules reference non-existent categories.');
        }
      } catch (error) {
        setParseError(error.message || 'Failed to parse CSV file');
        setParsedRules([]);
        setWarnings([]);
      }
    };
    reader.onerror = () => {
      setParseError('Failed to read file');
    };
    reader.readAsText(selectedFile);
  };

  const handleSelectAll = (checked) => {
    setSelectedRules(checked ? parsedRules.map((_, idx) => idx) : []);
  };

  const handleSelectRule = (index) => {
    setSelectedRules(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const handleImport = async () => {
    const rulesToImport = selectedRules.map(idx => parsedRules[idx]);
    setStep('importing');
    try {
      await onImport(rulesToImport);
      handleClose();
    } catch (error) {
      setParseError(error.message || 'Failed to import rules');
      setStep('review');
    }
  };

  const handleDownloadTemplate = () => {
    const template = getCSVTemplate();
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'rules_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClose = () => {
    setFile(null);
    setSelectedRules([]);
    setParseError(null);
    setWarnings([]);
    setParsedRules([]);
    setStep('upload');
    onClose();
  };

  const renderTemplateInfo = () => (
    <Stack spacing={2}>
      <Box sx={{ p: 1.5, backgroundColor: '#fff', borderRadius: 1.5, border: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle2" fontWeight="600" color="primary" sx={{ mb: 0.75 }}>
          Required Columns
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6, display: 'block' }}>
          <b>Category</b>: Category name (matched to your existing categories)<br />
          <b>Priority</b>: Number (1 = highest)<br />
          <b>Group</b>: Condition group number (same group = AND, different group = OR)<br />
          <b>Field</b>: e.g. description, amount, account<br />
          <b>Operator</b>: e.g. contains, equals, greater_than<br />
          <b>Value</b>: The value to match
        </Typography>
      </Box>
      <Box sx={{ p: 1.5, backgroundColor: '#fff', borderRadius: 1.5, border: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle2" fontWeight="600" color="primary" sx={{ mb: 0.75 }}>
          How It Works
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6, display: 'block' }}>
          Each row is one condition. Rows with the same Rule ID (or Rule Name), Category, and Priority are grouped into one rule. Rule Name and Rule ID columns are optional.
          <br /><br />
          Conditions in the <b>same Group</b> number use <b>AND</b> logic.<br />
          Conditions in <b>different Groups</b> use <b>OR</b> logic.
        </Typography>
      </Box>
      <Box sx={{ p: 1.5, backgroundColor: '#fff', borderRadius: 1.5, border: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle2" fontWeight="600" color="primary" sx={{ mb: 0.75 }}>
          Example
        </Typography>
        <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#555', lineHeight: 1.6, overflowX: 'auto', whiteSpace: 'pre' }}>
{`Rule Name,Category,Priority,Group,Field,Operator,Value
Grocery Shopping,Groceries,1,1,description,contains,Woolworths
Grocery Shopping,Groceries,1,1,amount,greater_than,20
Netflix,Subscriptions,2,1,description,contains,Netflix`}
        </Box>
      </Box>
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Header */}
      <DialogHeader
        title="Import Rules"
        subtitle="Upload a CSV file to import categorization rules"
        onClose={handleClose}
      />

      {/* Content */}
      <Box sx={{
        display: 'flex',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        flexDirection: { xs: 'column', md: 'row' }
      }}>
        {/* Left Panel */}
        <Box sx={{
          flex: { xs: 1, md: '0 0 60%' },
          overflow: 'auto',
          p: { xs: 2, sm: 3 },
          borderRight: { md: '2px solid #e0e0e0' },
          backgroundColor: '#fff'
        }}>
          {step === 'upload' && (
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CloudUploadIcon sx={{ color: '#F41468' }} />
                  Upload CSV File
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                  Select a CSV file with your rules. Rules will be merged with your existing rules.
                </Typography>

                <Box
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    border: '2px dashed',
                    borderColor: file ? '#4caf50' : '#ccc',
                    borderRadius: 2,
                    padding: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: file ? '#f0fdf4' : '#fafafa',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: '#F41468',
                      backgroundColor: '#fce4ec',
                    }
                  }}
                >
                  {file ? (
                    <>
                      <CheckCircleIcon sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{file.name}</Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>Click to change file</Typography>
                    </>
                  ) : (
                    <>
                      <CloudUploadIcon sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                        Click to select a CSV file
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#999' }}>
                        .csv files only
                      </Typography>
                    </>
                  )}
                </Box>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </Box>

              {parseError && (
                <Alert severity="error" icon={<ErrorIcon />}>
                  {parseError}
                </Alert>
              )}

              <Divider />

              <Box>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon sx={{ color: '#F41468' }} />
                  CSV Template
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                  Not sure how to format your file? Download the template below.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadTemplate}
                  sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 500 }}
                >
                  Download Template
                </Button>
              </Box>
            </Stack>
          )}

          {step === 'review' && (
            <Stack spacing={2}>
              <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ color: '#F41468' }} />
                Review Rules
              </Typography>

              {warnings.length > 0 && (
                <Alert severity="warning" icon={<WarningIcon />}>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                    {warnings.length} rule(s) skipped:
                  </Typography>
                  <Box component="ul" sx={{ mb: 0, pl: 2 }}>
                    {warnings.map((warning, idx) => (
                      <Typography key={idx} variant="caption" component="li" sx={{ display: 'block', mb: 0.25 }}>
                        {warning}
                      </Typography>
                    ))}
                  </Box>
                </Alert>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" fontWeight={500}>
                  {selectedRules.length} of {parsedRules.length} rules selected
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedRules.length === parsedRules.length && parsedRules.length > 0}
                      indeterminate={selectedRules.length > 0 && selectedRules.length < parsedRules.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      sx={{ color: '#F41468', '&.Mui-checked': { color: '#F41468' } }}
                    />
                  }
                  label="Select All"
                  sx={{ ml: 0 }}
                />
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#fce4ec' }}>
                      <TableCell padding="checkbox" width="50px">
                        <Checkbox
                          checked={selectedRules.length === parsedRules.length && parsedRules.length > 0}
                          indeterminate={selectedRules.length > 0 && selectedRules.length < parsedRules.length}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          sx={{ color: '#F41468', '&.Mui-checked': { color: '#F41468' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Rule</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Category</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Priority</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Conditions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedRules.map((rule, idx) => {
                      const totalConditions = rule.conditionGroups.reduce((sum, g) => sum + g.conditions.length, 0);
                      const firstCondition = rule.conditionGroups[0]?.conditions[0];
                      return (
                        <TableRow
                          key={idx}
                          hover
                          onClick={() => handleSelectRule(idx)}
                          sx={{ cursor: 'pointer', backgroundColor: selectedRules.includes(idx) ? '#fce4ec33' : 'transparent' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedRules.includes(idx)}
                              onChange={() => handleSelectRule(idx)}
                              sx={{ color: '#F41468', '&.Mui-checked': { color: '#F41468' } }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                            {rule.name || (firstCondition
                              ? `${firstCondition.field} ${firstCondition.operator} "${firstCondition.value}"`
                              : '—')}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>
                            <Chip label={rule.categoryName || rule.categoryId} size="small" variant="outlined" sx={{ borderColor: '#F41468', color: '#F41468' }} />
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                            <Chip label={rule.priority} size="small" sx={{ bgcolor: '#fce4ec', color: '#F41468', fontWeight: 600 }} />
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                            {totalConditions}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          )}

          {step === 'importing' && (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress sx={{ mb: 2, color: '#F41468' }} />
              <Typography variant="body1" fontWeight={500}>
                Importing {selectedRules.length} rule(s)...
              </Typography>
            </Box>
          )}
        </Box>

        {/* Right Panel — Template info */}
        <Box sx={{
          flex: { xs: 0, md: '0 0 40%' },
          overflow: 'auto',
          p: { xs: 2, sm: 3 },
          backgroundColor: '#fafafa',
          display: { xs: 'none', md: 'block' }
        }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon sx={{ color: '#F41468' }} />
            CSV Format Guide
          </Typography>
          {renderTemplateInfo()}
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        px: 3,
        py: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}>
        <Typography variant="body2" color="text.secondary">
          {step === 'upload' && 'Upload a CSV file to get started'}
          {step === 'review' && `${parsedRules.length} rule(s) found in file`}
          {step === 'importing' && 'Please wait...'}
        </Typography>
        <Stack direction="row" spacing={1.5}>
          {step === 'review' && (
            <Button
              onClick={() => { setStep('upload'); setFile(null); setParsedRules([]); setSelectedRules([]); }}
              color="inherit"
              sx={{ color: 'text.secondary' }}
            >
              Back
            </Button>
          )}
          <Button onClick={handleClose} color="inherit" sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          {step === 'review' && (
            <Button
              onClick={handleImport}
              variant="contained"
              disabled={selectedRules.length === 0 || isLoading}
              sx={{ px: 3 }}
            >
              Import {selectedRules.length} Rule{selectedRules.length !== 1 ? 's' : ''}
            </Button>
          )}
        </Stack>
      </Box>
    </Dialog>
  );
};

export default RulesImportDialog;
